/**
 * Service Worker Registration and Management
 * Handles SW lifecycle events and updates
 */

import { logger } from "./logger";

interface ServiceWorkerConfig {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
  onOffline?: () => void;
  onOnline?: () => void;
}

const SW_URL = "/sw.js";
const SW_SCOPE = "/";

/**
 * Register the service worker
 */
export async function registerServiceWorker(
  config: ServiceWorkerConfig = {},
): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) {
    logger.warn("Service workers are not supported in this browser");
    return null;
  }

  // Don't register in development mode by default
  const isDev = import.meta.env.DEV;
  if (isDev) {
    logger.info("Service worker registration skipped in development mode");
    // Still allow manual registration in dev if needed
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register(SW_URL, {
      scope: SW_SCOPE,
    });

    logger.info("Service worker registered", {
      scope: registration.scope,
    });

    // Handle different states
    if (registration.installing) {
      logger.debug("Service worker installing");
      trackInstalling(registration.installing, config);
    } else if (registration.waiting) {
      logger.debug("Service worker waiting");
      if (config.onUpdate) {
        config.onUpdate(registration);
      }
    } else if (registration.active) {
      logger.debug("Service worker active");
      if (config.onSuccess) {
        config.onSuccess(registration);
      }
    }

    // Listen for updates
    registration.addEventListener("updatefound", () => {
      const newWorker = registration.installing;
      if (newWorker) {
        trackInstalling(newWorker, config);
      }
    });

    // Listen for controller changes
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      logger.info("Service worker controller changed");
    });

    // Set up online/offline listeners
    if (config.onOffline) {
      window.addEventListener("offline", config.onOffline);
    }

    if (config.onOnline) {
      window.addEventListener("online", config.onOnline);
    }

    // Check for updates periodically (every hour)
    setInterval(
      () => {
        registration.update().catch((error) => {
          logger.error("Failed to check for SW updates", { error });
        });
      },
      60 * 60 * 1000,
    );

    return registration;
  } catch (error) {
    logger.error("Service worker registration failed", { error });
    return null;
  }
}

/**
 * Track service worker installation progress
 */
function trackInstalling(
  worker: ServiceWorker,
  config: ServiceWorkerConfig,
): void {
  worker.addEventListener("statechange", () => {
    logger.debug("Service worker state changed", { state: worker.state });

    switch (worker.state) {
      case "installed":
        if (navigator.serviceWorker.controller) {
          // New service worker available
          logger.info("New service worker installed, update available");
          if (config.onUpdate) {
            // Get registration to pass to callback
            navigator.serviceWorker.ready.then((registration) => {
              config.onUpdate!(registration);
            });
          }
        } else {
          // First install
          logger.info("Service worker installed for the first time");
          if (config.onSuccess) {
            navigator.serviceWorker.ready.then((registration) => {
              config.onSuccess!(registration);
            });
          }
        }
        break;

      case "redundant":
        logger.warn("Service worker became redundant");
        break;

      case "activated":
        logger.info("Service worker activated");
        break;
    }
  });
}

/**
 * Unregister all service workers
 */
export async function unregisterServiceWorker(): Promise<boolean> {
  if (!("serviceWorker" in navigator)) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration) {
      const success = await registration.unregister();
      if (success) {
        logger.info("Service worker unregistered");
      }
      return success;
    }
    return false;
  } catch (error) {
    logger.error("Failed to unregister service worker", { error });
    return false;
  }
}

/**
 * Skip waiting for new service worker
 */
export function skipWaiting(): void {
  if (navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({ type: "SKIP_WAITING" });
    logger.info("Sent SKIP_WAITING to service worker");
  }
}

/**
 * Check if there's a waiting service worker
 */
export async function checkForWaitingWorker(): Promise<boolean> {
  if (!("serviceWorker" in navigator)) {
    return false;
  }

  const registration = await navigator.serviceWorker.getRegistration();
  return !!registration?.waiting;
}

/**
 * Send message to service worker
 */
export function sendMessageToSW(message: Record<string, unknown>): void {
  if (navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage(message);
  } else {
    logger.warn("No service worker controller available");
  }
}

/**
 * Clear service worker caches
 */
export async function clearSWCaches(): Promise<void> {
  sendMessageToSW({ type: "CLEAR_CACHE" });
  logger.info("Sent CLEAR_CACHE to service worker");
}

/**
 * Get service worker registration status
 */
export async function getSWStatus(): Promise<{
  supported: boolean;
  registered: boolean;
  controller: boolean;
  waiting: boolean;
  installing: boolean;
  active: boolean;
}> {
  if (!("serviceWorker" in navigator)) {
    return {
      supported: false,
      registered: false,
      controller: false,
      waiting: false,
      installing: false,
      active: false,
    };
  }

  const registration = await navigator.serviceWorker.getRegistration();

  return {
    supported: true,
    registered: !!registration,
    controller: !!navigator.serviceWorker.controller,
    waiting: !!registration?.waiting,
    installing: !!registration?.installing,
    active: !!registration?.active,
  };
}
